import React, { useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { FormButton, SpinnerOverlay } from "components";
import {
  FormikSelectInput,
  FormikTextInput,
} from "components/UI/Inputs/FormikInputs";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { LARGO, mensajeLargo } from "consts/limites-campos";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";
import styles from "styles/pages/ModalActions.module.scss";
import { RNHFuncionUpd } from "services/services-mia/programa-salud/personal/RNHFuncionUpd";
import { RNHFuncionGet } from "services/services-mia/programa-salud/personal/RNHFuncionGet";
import { RNHAgrupamientoGet } from "services/services-mia/programa-salud/personal/RNHAgrupamientoGet";

/**
 * EL AGRUPAMIENTO LLEGA COMO NOMBRE Y SE GUARDA COMO ID.
 *
 * `rnhFuncionAgrupamientoId` termina en "Id" pero su contenido es el NOMBRE:
 * "Agrupamiento de Cocina", no un numero. El `<select>`, en cambio, trabaja con el id
 * —es lo que el Upd necesita—, asi que el valor que llega no matchea ninguna opcion y
 * el campo abria en "Sin agrupamiento" aunque la funcion tuviera uno.
 *
 * Por eso la precarga TRADUCE: busca el nombre en el catalogo y se queda con su id.
 *
 * El match es por nombre y eso es fragil —dos agrupamientos homonimos, un espacio de
 * mas—. Lo correcto seria que la response trajera el id; mientras no lo haga, esto es
 * lo unico que hay. Si el nombre no aparece en el catalogo, el campo queda vacio en
 * vez de inventar una seleccion.
 *
 * Las dos cosas se piden JUNTAS: traducir necesita el catalogo, y si cada una llega
 * por su lado la precarga puede correr antes de tener contra que comparar.
 */
export default function UpdateModal({ row, closeModal, updateList }) {
  const setSnackBarInfo = useSetRecoilState(snackbarData);
  const credentials = useRecoilValue(userCredentials);
  const [data, setData] = useState();
  const [inRequest, setInRequest] = useState(false);
  const formikRef = useRef();

  const [agrupamientos, setAgrupamientos] = useState([]);

  const getFuncionData = async () => {
    setInRequest(true);
    const [funcion, catalogo] = await Promise.all([
      RNHFuncionGet(credentials, { id: row.id }),
      RNHAgrupamientoGet(credentials),
    ]);

    // Sin esto el select lista la fila centinela del back como si fuera una opcion.
    const opciones = catalogo.data?.[0]?.rnhAgrupamientoId ? catalogo.data : [];
    setAgrupamientos(opciones);

    const result = funcion.data;
    const encontrado = Array.isArray(result)
      ? result.find((c) => String(c.rnhFuncionId) === String(row.id))
      : null;
    setInRequest(false);


    if (!encontrado) {
      setSnackBarInfo({
        message: "No se pudo cargar la función seleccionada.",
        severity: "error",
        open: true,
      });
      closeModal();
      return;
    }

    formikRef.current.setValues({
      nombre: encontrado.rnhFuncionNombre ?? "",
      cod: encontrado.rnhFuncionCod ?? "",
      // Del nombre al id, contra el catalogo. A string porque el <select> nativo
      // compara su value como texto y el catalogo trae numeros.
      agrupamientoId: String(
        opciones.find(
          (a) => a.rnhAgrupamientoNombre === encontrado.rnhFuncionAgrupamientoId
        )?.rnhAgrupamientoId ?? ""
      ),
    }, false);
    setData(encontrado);
  };

  const guardarFuncion = async (values) => {
    setInRequest(true);
    const response = await RNHFuncionUpd(credentials, {
      // Lo recien leido manda; la fila de la grilla es el respaldo por si el Get fallo.
      id: data?.rnhFuncionId ?? row.id,
      cod: values.cod,
      nombre: values.nombre,
      agrupamientoId: values.agrupamientoId,
      version: data?.rnhFuncionVersion ?? row.version,
    });

    setInRequest(false);
    if (response.ok) {
      setSnackBarInfo({
        message: "Funcion modificada correctamente",
        severity: "success",
        open: true,
      });
      
      formikRef.current.resetForm();
      updateList();
      closeModal();
    }
  };

  useEffect(() => {
    getFuncionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        nombre: row?.nombre ?? "",
        cod: row?.cod ?? "",
        // Arranca vacio: la fila de la grilla guarda el NOMBRE, no el id, y el
        // loader lo resuelve apenas vuelve el catalogo.
        agrupamientoId: "",
      }}
      validationSchema={yup.object({
        nombre: yup
          .string()
          .max(LARGO.nombre, mensajeLargo(LARGO.nombre))
          .required("Debe ingresar un nombre"),
        cod: yup
          .string()
          .max(LARGO.cod, mensajeLargo(LARGO.cod))
          .required("Debe ingresar un código"),
        // Es string: el <select> entrega strings y el id se convierte en el servicio.
        agrupamientoId: yup.string(),
      })}
      onSubmit={(values) => {
        guardarFuncion(values);
      }}
    >
      <Form className={styles.inputs} noValidate>
        <div className={styles["column"]}>
          <Grid
            container
            rowSpacing={0}
            columnSpacing={2}
            sx={{
              width: "100%",
              "& .MuiFormControl-root": { marginTop: 0, marginBottom: 0 },
            }}
          >
            <Grid item xs={4}>
              <FormikTextInput
                fullWidth={true}
                name="cod"
                labelText="Código"
                  inputProps={{ maxLength: LARGO.cod }}
              />
            </Grid>
            <Grid item xs={8}>
              <FormikTextInput
                fullWidth={true}
                name="nombre"
                labelText="Nombre"
                  inputProps={{ maxLength: LARGO.nombre }}
              />
            </Grid>
            <Grid item xs={12}>
              {/* No es obligatorio: una funcion puede no pertenecer a ningun
                  agrupamiento. Por eso la opcion vacia NO va `disabled` como en otros
                  selects del modulo: tiene que poder elegirse para dejarlo sin
                  agrupamiento, y tambien para sacarle el que ya tenia. */}
              <FormikSelectInput
                name="agrupamientoId"
                labelText="Agrupamiento"
                disabled={inRequest}
              >
                <option value="">Sin agrupamiento</option>
                {agrupamientos.map((a) => (
                  <option key={a.rnhAgrupamientoId} value={a.rnhAgrupamientoId}>
                    {a.rnhAgrupamientoNombre}
                  </option>
                ))}
              </FormikSelectInput>
            </Grid>
          </Grid>
          <FormButton
            inRequest={inRequest}
            newFormData={{ button: "Modificar" }}
            onCloseModal={closeModal}
          />
          {(inRequest) && <SpinnerOverlay />}
        </div>
      </Form>
    </Formik>
  );
}
